// REL_1.1.14 F-262 / S-981 — Ported from deedo-frontend ForBuyersPage.tsx (REL_1.1.12 design).
// Differences: added <link rel="canonical"> → /for-buyers (REL_1.1.15 S-1101 v2.8 — `/us/` prefix dropped).
import { Helmet } from "react-helmet-async";
import { QrCode } from "lucide-react";

const MARKETING_BASE = import.meta.env.VITE_MARKETING_BASE_URL || "https://www.deedo.ai";

export default function ForBuyers() {
  return (
    <>
      <Helmet>
        <title>For Buyers — Deedo</title>
        <meta
          name="description"
          content="Explore homes effortlessly with Deedo. Your 24/7 personal property assistant — no downloads, no waiting, just answers."
        />
        <link rel="canonical" href={`${MARKETING_BASE}/for-buyers`} />
        <meta property="og:title" content="For Buyers — Deedo" />
        <meta property="og:description" content="Scan a QR code at any open house and get instant answers about the property — no app required." />
        <meta property="og:url" content={`${MARKETING_BASE}/for-buyers`} />
        <meta property="og:type" content="website" />
      </Helmet>

      <section className="relative bg-gradient-to-br from-[var(--brand-primary)] via-[var(--brand-primary-light)] to-[var(--brand-primary)] text-white">
        <div className="mx-auto max-w-4xl px-4 py-20 text-center lg:px-8 lg:py-24">
          <h1 className="text-4xl font-extrabold tracking-tight lg:text-5xl">
            Explore Homes Effortlessly
          </h1>
          <p className="mx-auto mt-5 max-w-2xl text-lg leading-relaxed text-blue-100">
            Your 24/7 personal property assistant. No downloads. No waiting. Just answers.
          </p>
        </div>
        <div className="h-20 lg:h-24" />
      </section>

      <section className="bg-gray-50 pb-24">
        <div className="mx-auto -mt-32 max-w-5xl px-4 lg:px-8 lg:-mt-40">
          <div className="rounded-2xl bg-white p-8 shadow-xl lg:p-14">
            {/* Step 1 — copy left, visual right */}
            <div className="grid gap-10 lg:grid-cols-2 lg:gap-12">
              <div>
                <span className="flex h-9 w-9 items-center justify-center rounded-full bg-[var(--brand-secondary)]/20 text-sm font-bold text-[var(--brand-primary)]">
                  1
                </span>
                <h2 className="mt-5 text-2xl font-bold text-[var(--brand-primary)] lg:text-3xl">
                  The Magic of the Scan
                </h2>
                <p className="mt-4 leading-relaxed text-gray-600">
                  Forget carrying around stacks of paper flyers. When you see a Deedo QR code at a
                  property, simply open your smartphone camera. You don&apos;t need to download any
                  special app or create an account to start asking questions.
                </p>
              </div>
              <div className="flex items-center justify-center">
                <div className="flex aspect-square w-full max-w-xs items-center justify-center rounded-2xl bg-gray-100">
                  <QrCode className="h-32 w-32 text-[var(--brand-primary)]" aria-hidden="true" />
                </div>
              </div>
            </div>

            {/* Step 2 — visual left, copy right (alternates) */}
            <div className="mt-16 grid gap-10 lg:grid-cols-2 lg:gap-12">
              <div className="order-2 flex items-center justify-center lg:order-1">
                <ChatMockup />
              </div>
              <div className="order-1 lg:order-2">
                <span className="flex h-9 w-9 items-center justify-center rounded-full bg-[var(--brand-secondary)]/20 text-sm font-bold text-[var(--brand-primary)]">
                  2
                </span>
                <h2 className="mt-5 text-2xl font-bold text-[var(--brand-primary)] lg:text-3xl">
                  Ask Anything
                </h2>
                <p className="mt-4 leading-relaxed text-gray-600">
                  &ldquo;When was the roof last replaced?&rdquo; &ldquo;What are the monthly HOA fees?&rdquo; &ldquo;Are pets
                  allowed?&rdquo; Deedo&apos;s AI has read all the property documents and MLS data, giving
                  you instant, accurate answers while you are still standing in the living room.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>
    </>
  );
}

function ChatMockup() {
  return (
    <div className="w-full max-w-sm rounded-2xl bg-gray-100 p-5">
      <div className="rounded-xl bg-white p-4 shadow-sm">
        <div className="flex justify-end">
          <div className="max-w-[80%] rounded-lg bg-[var(--brand-primary)] px-3 py-2 text-xs font-medium text-white">
            When were the floors done?
          </div>
        </div>
        <div className="mt-3 flex justify-start">
          <div className="max-w-[80%] rounded-lg border border-gray-200 bg-white px-3 py-2 text-xs text-gray-700">
            The hardwood floors were fully refinished in June 2023.
          </div>
        </div>
      </div>
    </div>
  );
}
